import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './PassengerWeightsPage.css';

const PassengerWeightsPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const thankYouMessage = location.state?.thankYouMessage;
  const revolutLink = location.state?.revolut_link;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passengerCount, setPassengerCount] = useState(0);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [weights, setWeights] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setError('');
      setLoading(true);
      try {
        const { data } = await axios.get(`/api/booking/passenger-weights/${encodeURIComponent(token)}`);
        if (cancelled) return;
        const n = Math.max(1, Number(data.passengerCount) || 1);
        setPassengerCount(n);
        setAlreadySubmitted(Boolean(data.submitted));
        if (data.submitted && Array.isArray(data.weights) && data.weights.length === n) {
          setWeights(data.weights.map(String));
        } else {
          setWeights(Array.from({ length: n }, () => ''));
        }
        setNotes(data.notes || '');
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || t('passengerWeights.errLoad'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) load();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const setWeightAt = (index, value) => {
    setWeights((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const nums = weights.map((w) => {
      const x = parseFloat(String(w).replace(',', '.'));
      return Number.isNaN(x) ? NaN : x;
    });
    if (nums.some((x) => Number.isNaN(x))) {
      setError(t('passengerWeights.errAllRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`/api/booking/passenger-weights/${encodeURIComponent(token)}`, {
        weights: nums,
        notes: notes.trim() || undefined
      });
      navigate('/booking/thank-you?type=booking', {
        state: { message: thankYouMessage, revolut_link: revolutLink }
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('passengerWeights.errSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  const goThankYou = () => {
    navigate('/booking/thank-you?type=booking', {
      state: { message: thankYouMessage, revolut_link: revolutLink }
    });
  };

  if (loading) {
    return (
      <div className="passenger-weights-page container">
        <p className="passenger-weights-loading">{t('passengerWeights.loading')}</p>
      </div>
    );
  }

  if (error && !passengerCount) {
    return (
      <div className="passenger-weights-page container">
        <div className="error" role="alert">{error}</div>
        <Link to="/" className="btn btn-secondary">{t('thankYou.backHome')}</Link>
      </div>
    );
  }

  return (
    <div className="passenger-weights-page container">
      <h1>{t('passengerWeights.title')}</h1>
      <p className="passenger-weights-intro">{t('passengerWeights.intro')}</p>

      {error && <div className="error" role="alert">{error}</div>}

      {alreadySubmitted ? (
        <div className="card passenger-weights-card">
          <p>{t('passengerWeights.alreadySubmitted')}</p>
          <div className="passenger-weights-actions">
            <button type="button" className="btn btn-primary" onClick={goThankYou}>
              {t('passengerWeights.continueToThankYou')}
            </button>
            <Link to="/" className="btn btn-secondary">{t('thankYou.backHome')}</Link>
          </div>
        </div>
      ) : (
        <form className="card passenger-weights-card" onSubmit={handleSubmit} noValidate>
          <h2 className="passenger-weights-form-title">{t('passengerWeights.formTitle', { count: passengerCount })}</h2>
          <ul className="passenger-weights-list">
            {Array.from({ length: passengerCount }, (_, i) => (
              <li key={i} className="passenger-weights-row">
                <label htmlFor={`pax-weight-${i}`}>
                  {t('passengerWeights.passengerLabel', { index: i + 1 })}
                </label>
                <div className="passenger-weights-input-wrap">
                  <input
                    id={`pax-weight-${i}`}
                    type="number"
                    inputMode="decimal"
                    min="15"
                    max="250"
                    step="1"
                    required
                    value={weights[i] ?? ''}
                    onChange={(e) => setWeightAt(i, e.target.value)}
                    placeholder={t('passengerWeights.kgPlaceholder')}
                  />
                  <span className="passenger-weights-unit">kg</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="form-group">
            <label htmlFor="pax-notes">{t('passengerWeights.notesLabel')}</label>
            <textarea
              id="pax-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('passengerWeights.notesPlaceholder')}
            />
          </div>
          <div className="passenger-weights-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('passengerWeights.submitting') : t('passengerWeights.submit')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PassengerWeightsPage;
